import React, { useCallback, useEffect, useState, useMemo } from 'react'
import {
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import {
  AccessTimeOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  InfoOutlined,
  LocationOnOutlined,
  PersonOutline,
  ScheduleOutlined,
  WorkOutline
} from '@material-ui/icons'
import moment from 'moment'
import localization from 'moment/locale/pt-br'
import useStyles from './styles'
import { useCadaverService } from 'hooks/useCadaverService'
import { ThemeProvider } from '@material-ui/styles'
import { CssBaseline } from '@material-ui/core'
import { getTheme } from '../../../theme'

export const ListaCadaveresPublica = (props) => {
  const lightTheme = useMemo(() => getTheme(false), [])

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <ListaCadaveresPublicaContent {...props} />
    </ThemeProvider>
  )
}

export const ListaCadaveresPublicaContent  = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const [cadaveresHoje, setCadaveresHoje] = useState([])
  const [cadaveresOntem, setCadaveresOntem] = useState([])
  const classes = useStyles()
  const cadaverService = useCadaverService()
  const [erro, setErro] = useState(false)
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(new Date())
  const [carregando, setCarregando] = useState(true)

  const dataHoje = moment().format('DD [de] MMMM [de] YYYY')
  const dataOntem = moment().subtract(1, 'day').format('DD [de] MMMM [de] YYYY')

  const fetchCadaveres = useCallback(async () => {
    const responseCadaveresHoje = await cadaverService.listarCadaveresRotaPublicaDiaAtualAsync()
    const responseCadaveresOntem = await cadaverService.listarCadaveresRotaPublicaDiaAnteriorAsync()
    setCadaveresHoje(responseCadaveresHoje)
    setCadaveresOntem(responseCadaveresOntem)
    setCarregando(false)
  }, [])

  useEffect(() => {
    fetchCadaveres()
  }, [fetchCadaveres])

  return (
    <main className={classes.root}>
      <div className={classes.content}>
        <Cabecalho classes={classes} />

        <AvisoInformativo classes={classes} />

        {carregando ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={36} />
            <Typography className={classes.loadingText}>
              Carregando registros...
            </Typography>
          </div>
        ) : erro ? (
          <div className={classes.errorContainer}>
            <Typography className={classes.errorTitle}>
              Não foi possível carregar os registros.
            </Typography>

            <Typography className={classes.errorText}>
              Tente atualizar a página novamente em alguns instantes.
            </Typography>
          </div>
        ) : (
          <>
            <CardListagem
              titulo={dataHoje}
              cadaveres={cadaveresHoje}
              classes={classes}
            />

            <CardListagem
              titulo={dataOntem}
              cadaveres={cadaveresOntem}
              classes={classes}
            />
          </>
        )}

        <Rodape
          classes={classes}
          ultimaAtualizacao={ultimaAtualizacao}
        />
      </div>
    </main>
  )
}

const Cabecalho = ({ classes }) => (
  <header className={classes.header}>
    <img
      className={classes.logo}
      alt="Logo do SIGEP"
      src="/images/logos/logo-sigep.png"
    />

    <div className={classes.headerText}>
      
      <Typography component="h1" className={classes.systemName}>
        Sistema Integrado de Gestão de Perícias
      </Typography>

      <Typography className={classes.systemName}>
        Listagem Pública de Óbitos
      </Typography>

      <Typography className={classes.institutionName}>
        Polícia Científica do Rio Grande do Norte
      </Typography>

      

    </div>
  </header>
)

const AvisoInformativo = ({ classes }) => (
  <section className={classes.infoCard}>
    <div className={classes.infoIconContainer}>
      <InfoOutlined className={classes.infoIcon} />
    </div>

    <div className={classes.infoContent}>
      <Typography component="h2" className={classes.infoTitle}>
        Informações importantes
      </Typography>

      <Typography className={classes.infoText}>
        Os dados apresentados são provenientes de informações constantes
        na Guia de Solicitação de Exame.
      </Typography>

      <Typography className={classes.infoText}>
        As iniciais do nome, sexo e idade poderão ser atualizadas após os
        resultados dos exames de identificação realizados pela Polícia
        Científica do RN, incluindo exame necropapiloscópico, identificação
        odontológica ou DNA.
      </Typography>
    </div>

    <img
      src="/images/logos/policia_cientifica_brasao.png"
      alt="Brasao da Polícia Científica do RN"
      aria-hidden="true"
      className={classes.watermark}
      onError={(event) => {
        event.currentTarget.style.display = 'none'
      }}
    />
  </section>
)

const CardListagem = ({ titulo, cadaveres, classes }) => {
  const quantidade = cadaveres.length

  return (
    <section className={classes.listCard}>
      <div className={classes.listCardHeader}>
        <div className={classes.dateIconContainer}>
          <CalendarTodayOutlined className={classes.metaIcon} />
          
        </div>

        <Typography component="h2" className={classes.dateTitle}>
          {titulo}
        </Typography>

        <span
          className={
            quantidade > 0
              ? classes.recordCountActive
              : classes.recordCountEmpty
          }
        >
          {quantidade} {quantidade === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {quantidade > 0 ? (
        <TabelaCadaveres
          cadaveres={cadaveres}
          classes={classes}
        />
      ) : (
        <EstadoVazio classes={classes} />
      )}
    </section>
  )
}

const EstadoVazio = ({ classes }) => (
  <div className={classes.emptyState}>
    <div className={classes.emptyIconContainer}>
      <WorkOutline className={classes.emptyIcon} />
    </div>

    <div>
      <Typography className={classes.emptyTitle}>
        Nenhum óbito registrado nesta data.
      </Typography>

      <Typography className={classes.emptyText}>
        Não há registros disponíveis para este dia.
      </Typography>
    </div>
  </div>
)

const TabelaCadaveres = ({ cadaveres, classes }) => (
  <TableContainer className={classes.tableContainer}>
    <Table
      aria-label="Listagem pública de óbitos"
      className={classes.table}
    >
      <TableHead>
        <TableRow className={classes.tableHeader}>
          <TableCell className={classes.entryColumn}>
            <CabecalhoColuna
              icon={<AccessTimeOutlined />}
              texto="Entrada"
              classes={classes}
            />
          </TableCell>

          <TableCell className={classes.nameColumn}>
            <CabecalhoColuna
              icon={<PersonOutline />}
              texto="Iniciais do nome"
              classes={classes}
            />
          </TableCell>

          <TableCell className={classes.locationColumn}>
            <CabecalhoColuna
              icon={<LocationOnOutlined />}
              texto="Local da ocorrência"
              classes={classes}
            />
          </TableCell>

          <TableCell className={classes.causeColumn}>
            <CabecalhoColuna
              icon={<WorkOutline />}
              texto="Causa preliminar da morte"
              classes={classes}
            />
          </TableCell>

          <TableCell className={classes.cityColumn}>
            <CabecalhoColuna
              icon={<BusinessOutlined />}
              texto="Município"
              classes={classes}
            />
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {cadaveres.map((cadaver, index) => (
          <LinhaTabelaCadaver
            key={cadaver.id || cadaver._id || `${cadaver.createdAt}-${index}`}
            cadaver={cadaver}
            classes={classes}
          />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const CabecalhoColuna = ({ icon, texto, classes }) => (
  <div className={classes.columnHeader}>
    {React.cloneElement(icon, {
      className: classes.columnHeaderIcon
    })}

    <Typography component="span" className={classes.columnHeaderText}>
      {texto}
    </Typography>
  </div>
)

const LinhaTabelaCadaver = ({ cadaver, classes }) => {
  const endereco = formatarEndereco(cadaver?.endOrig)

  return (
    <TableRow className={classes.tableRow}>
      <TableCell className={classes.tableCell}>
        <Typography className={classes.cellText}>
          {formatarHorarioEntrada(cadaver?.createdAt)}
        </Typography>
      </TableCell>

      <TableCell className={classes.tableCell}>
        <Typography className={classes.cellText}>
          {exibirIniciais(cadaver?.nome)}
        </Typography>
      </TableCell>

      <TableCell className={classes.tableCell}>
        <Typography className={classes.cellText}>
          {endereco}
        </Typography>
      </TableCell>

      <TableCell className={classes.tableCell}>
        <Typography className={classes.cellText}>
          {cadaver?.causaJuridicaMortePreliminar || 'Não informada'}
        </Typography>
      </TableCell>

      <TableCell className={classes.tableCell}>
        <Typography className={classes.cityText}>
          {cadaver?.endOrig?.cidade?.toUpperCase() || 'NÃO INFORMADO'}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

const Rodape = ({ classes, ultimaAtualizacao }) => (
  <footer className={classes.footer}>
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} md={4}>
        <div className={classes.footerItem}>
          <div className={classes.footerIconContainer}>
        
            <img
              src="/images/logos/policia-cientifica-brasao.png"
              alt="Polícia Científica do Rio Grande do Norte"
              className={classes.footerLogo}
              onError={(event) => {
                event.currentTarget.style.display = 'none'
              }}
            />
          </div>

          <div>
            <Typography className={classes.footerTitle}>
              Polícia Científica do Rio Grande do Norte
            </Typography>

            <Typography className={classes.footerText}>
              Núcleo de Tecnologia da Informação
            </Typography>
          </div>
        </div>
      </Grid>

      <Grid item xs={12} md={4}>
        <div className={classes.footerItem}>
          <ScheduleOutlined className={classes.footerIcon} />

          <div>
            <Typography className={classes.footerText}>
              Atualizado automaticamente às{' '}
              <strong>
                {moment(ultimaAtualizacao).format('HH:mm')}
              </strong>
            </Typography>

            <Typography className={classes.footerDate}>
              {moment(ultimaAtualizacao).format('DD/MM/YYYY')}
            </Typography>
          </div>
        </div>
      </Grid>
    </Grid>
  </footer>
)

function exibirIniciais(nome) {
  if (!nome || typeof nome !== 'string') {
    return 'AINDA NÃO IDENTIFICADO'
  }

  const iniciais = nome
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((palavra) => `${palavra.charAt(0).toUpperCase()}.`)

  return iniciais.length
    ? iniciais.join(' ')
    : 'AINDA NÃO IDENTIFICADO'
}

function formatarEndereco(endereco) {
  if (!endereco) {
    return 'LOCAL NÃO INFORMADO'
  }

  const partes = []

  if (endereco.rua) {
    partes.push(endereco.rua)
  }

  if (endereco.numero) {
    partes.push(endereco.numero)
  }

  if (endereco.bairro) {
    partes.push(endereco.bairro)
  }

  return partes.length
    ? partes.join(', ').toUpperCase()
    : 'LOCAL NÃO INFORMADO'
}

function formatarHorarioEntrada(data) {
  if (!data || !moment(data).isValid()) {
    return '--:--'
  }

  return moment(data).format('HH:mm')
}
